<template>
  <div>
    <filter-manager
      v-model="model"
      class="mb-4 filter-manager-container"
      :filters="filtersConfig"
      @providerType="onChangeFilterHandler"
      @search="onSearchHandler"
      @status="onChangeFilterHandler"
    />
    <custom-separator class="my-4" text="Resultado de Proveedores" />
    <custom-result-not-found v-if="results.length == 0" />
    <template v-else>
      <provider-result-row
        v-for="(row, idx) of results"
        :key="`result-${idx}`"
        class="mb-3"
        :data="row"
        :index="idx"
        :opened="idx === activeIdx"
        @on-view-documents="onViewDocumentsHandler(row)"
        @on-view-provider="onViewProviderHandler(row)"
        @on-view-provider-profile="onViewProviderProfileHandler(row)"
        @set-active-index="activeIdx = $event"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        show-text
        @pagination-go-page="goToPageHandler"
      />
    </template>
  </div>
</template>

<script>
import { ProviderResultRow } from '.';
import FilterManager from 'FilterManager';
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import {
  getProviderInitFilters as _getProviderInitFilters,
  getProviders,
  getProviderOptions as _getProviderOptions,
  getCompanyById as _getCompanyById,
} from '@/services/provider/provider.api.js';
import providerTypes from '@/utils/constants/providerTypes.js';

//constants
const PROVIDER_CONTRACT_DOCUMENT_ROUTE_NAME = 'ProviderContractDocument';
const DEFAULT_PAGE_SIZE = 20;
const PROVIDER_MANAGEMENT_ROUTE_NAME = 'ProviderManagement';

export default {
  name: 'ProvidersTray',
  mixins: [storeMixin],
  components: {
    ProviderResultRow,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
  },
  data: () => ({
    activeIdx: null,
    model: {
      providerType: null,
      status: null,
      provider: null,
      ssn: null,
      fiscalYear: null,
      documentStatus: null,
    },
    filterOptions: {
      providerTypes: [],
      statuses: [],
      providers: [],
      fiscalYears: [],
      documentStatuses: [],
    },
    pagination: {
      currentPage: 1,
      totalItemCount: 1,
      pageCount: 1,
      count: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    results: [],
  }),
  computed: {
    filtersConfig() {
      return [
        {
          name: 'providerType',
          component: 'CustomDropDown',
          options: this.filterOptions.providerTypes,
          placeholder: 'Tipo de Proveedor',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: this.userHasSingleCompany
            ? 'col-12 col-md-4'
            : 'col-12 col-md-3',
          disabled: this.userHasSingleCompany,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filterOptions.statuses,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-2',
          visible: !this.userHasSingleCompany,
        },
        {
          name: 'provider',
          component: 'CustomDropDown',
          options: this.filterOptions.providers,
          placeholder: 'Proveedor',
          fieldName: 'name',
          fieldKey: 'id',
          class: this.userHasSingleCompany
            ? 'col-12 col-md-4'
            : 'col-12 col-md-3',
          clearable: true,
          disabled: this.userHasSingleCompany,
        },
        {
          name: 'ssn',
          component: 'CustomInput',
          icon: 'fas fa-search color-primary-2',
          leftIcon: true,
          placeholder: 'Escriba Número de Seguro Social Patronal',
          class: 'col-12 col-md-4',
        },
        {
          name: 'fiscalYear',
          component: 'CustomDropDown',
          options: this.filterOptions.fiscalYears,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          label: 'Año Fiscal de Solicitud:',
          isAdvanced: true,
        },
        {
          name: 'documentStatus',
          component: 'CustomDropDown',
          options: this.filterOptions.documentStatuses,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          label: 'Estatus de documentos de la Solicitud:',
          isAdvanced: true,
        },
      ];
    },
    userHasSingleCompany() {
      return this.userCompanies?.length === 1;
    },
    userCurrentCompany() {
      return this.userHasSingleCompany ? this.userCompanies[0] : null;
    },
    userCurrentCompanyType() {
      const company = this.userCompanies[0];
      return this.userHasSingleCompany
        ? {
            id: company.providerTypeId,
            name: company.providerTypeName,
          }
        : null;
    },
    userHasCompanies() {
      return this.userCompanies.length > 0;
    },
    hasPersistInfo() {
      return Boolean(this.moduleData?.ProviderMain || false);
    },
  },
  async mounted() {
    await this.getInitFilters();
    await this.getProviderOptions({
      companyType: null,
      status: null,
    });
    await this.onSearchHandler();
  },
  methods: {
    async getInitFilters() {
      const { filters, collections } = (await _getProviderInitFilters()).data;
      if (Object.values(filters).length && Object.values(collections).length) {
        this.model = {
          ...this.model,
          ...filters,
          provider: this.userCurrentCompany ?? this.model.provider,
          providerType: this.userCurrentCompanyType ?? this.model.providerType,
          status: this.hasPersistInfo ? this.model.status : null,
        };

        if (this.userHasSingleCompany) {
          const companyInformationResult = await _getCompanyById(
            this.model?.provider?.id
          );
          const company = companyInformationResult.data.company;

          const isServiceAssistant = company.isServiceAssistant;

          if (isServiceAssistant) {
            this.model.providerType.id = providerTypes.Asistente;
            this.model.providerType.name = 'Asistente';
          }
        }

        this.filterOptions = {
          ...this.filterOptions,
          ...collections,
          providers: this.userCompanies?.length
            ? this.userCompanies
            : collections.providers,
        };
      }
    },
    async getProviderOptions(payload) {
      const { data } = await _getProviderOptions(payload);
      if (!this.userHasCompanies) {
        this.filterOptions.providers = data;
      }
    },
    onChangeFilterHandler() {
      if (!this.userHasCompanies) {
        const filters = this.fillPayload();
        this.model.provider = null;
        this.getProviderOptions({
          companyType: filters.providerType,
          status: filters.status,
        });
      }
    },
    async searchProvider(payload) {
      const res = (await getProviders(payload)).data;
      this.results = res.data;
      this.pagination = {
        ...this.pagination,
        count: res.count,
        currentPage: res.currentPage,
        pageCount: res.pageCount,
        totalItemCount: res.totalItemCount,
      };
    },
    onSearchHandler(pageIndex = this.pagination.currentPage) {
      const payload = this.fillPayload(pageIndex);
      this.searchProvider(payload);
    },
    async goToPageHandler(page) {
      await this.onSearchHandler(page);
    },
    onViewDocumentsHandler({ id: companyId, academicYearId }) {
      if (companyId && academicYearId) {
        this.$router.push({
          name: PROVIDER_CONTRACT_DOCUMENT_ROUTE_NAME,
          params: {
            companyId,
            academicYearId,
          },
        });
      }
    },
    onViewProviderHandler({ id: companyId }) {
      if (companyId) {
        this.$router.push({
          name: PROVIDER_MANAGEMENT_ROUTE_NAME,
          params: {
            companyId,
          },
        });
      }
    },
    onViewProviderProfileHandler({
      id: companyId,
      academicYearId: academicYearId,
    }) {
      let currentPage = this.pagination.currentPage;
      this.$router.push({
        name: PROVIDER_CONTRACT_DOCUMENT_ROUTE_NAME,
        params: {
          companyId,
          academicYearId,
          currentPage,
        },
      });
    },
    fillPayload(page) {
      const payload = {
        status: this.model?.status?.id,
        providerType: this.model?.providerType?.id,
        companyId: this.model?.provider?.id,
        ssn: this.model?.ssn,
        fiscalYearId: this.model?.fiscalYear?.id,
        documentStatusId: this.model?.documentStatus?.id,
        pageIndex: page ? page : 1,
        pageSize: this.pagination.pageSize,
      };
      return payload;
    },
  },
};
</script>

<style scoped lang="scss">
.filter-manager-container ::v-deep {
  @media (min-width: 992px) {
    .col-lg-3 {
      flex: 0 0 16.66666667%;
      max-width: 16.66666667%;
    }
  }
}
</style>
